import React, { useEffect, useState, useRef, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { Order } from '../../types';
import { fmtMoney } from '../../utils/formatMoney';
import { slipMismatchInfo } from '../../utils/slipMismatch';
import bankLogos from 'thai-banks-logo/banks-logo.json';
import { toast } from 'react-toastify';
import socketService from '../../services/socketService';
import soundService from '../../services/soundService';
import { useOrderNotification } from '../../hooks/useOrderNotification';
import { useServerPagination } from '../../hooks/useServerPagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faHourglass, faCheckCircle, faTimesCircle, faLink, faRotate,
  faEye, faPaperclip, faBell, faFileAlt, faCamera, faUpload,
  faExclamationTriangle, faCreditCard, faClipboardList, faSearch, faXmark,
  faFilter, faBan, faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { LiveViewToggle, LiveModeBufferBadge, CountdownTimer, SlipAttemptTimeline, slipAttemptUrls } from '../../components/shared';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import dayjs from 'dayjs';
import { Tooltip } from 'react-tooltip';


const formatSlipUrl = (slipUrl: string) => {
  if (!slipUrl) return '';
  if (!slipUrl.startsWith('http') && !slipUrl.startsWith('data:')) {
    return `data:image/png;base64,${slipUrl}`;
  }
  return slipUrl;
};

const ORDER_STATUS_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faHourglass,    label: 'Pending'   },
  paid:      { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle,  label: 'Paid'      },
  expired:   { bg: 'bg-gray-100',  text: 'text-gray-600',  icon: faTimesCircle,  label: 'Expired'   },
  cancelled: { bg: 'bg-red-100',   text: 'text-red-600',   icon: faTimesCircle,  label: 'Cancelled' },
};

const SLIP_METHOD_MAP: Record<string, { label: string; detail: string; className: string }> = {
  standard:  { label: '✓ เช็คครบ',       detail: 'เช็คบัญชีผู้รับ + ธนาคาร + ยอดเงิน + เศษสตางค์', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  no_satang: { label: '✓ ไม่เช็คสตางค์',  detail: 'เช็คบัญชีผู้รับ + ธนาคาร + ยอดเงิน (ข้ามเศษสตางค์)', className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  no_verify: { label: '– ระบบตรวจปิดอยู่', detail: 'อนุมัติโดยไม่ผ่านระบบตรวจสลิป (ตั้งค่าปิดอยู่ / ไม่มี API key)', className: 'bg-gray-200 text-gray-600 dark:bg-slate-600 dark:text-slate-300' },
  force:     { label: '⚠ ข้ามคำเตือน',    detail: 'ระบบตรวจสลิปไม่ผ่าน แต่แอดมินยืนยันอนุมัติต่อ', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  skip_all:  { label: '✗ ไม่เช็คเลย',     detail: 'ข้ามการตรวจสอบทั้งหมด (เช็คเฉพาะ QR สลิปซ้ำในระบบ)', className: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
};

const isAdminApproved = (by?: string | null): boolean =>
  !!by && by !== 'customer_upload' &&
  !by.startsWith('telegram:') && !by.startsWith('api:') && !by.startsWith('slip-retry:');

const AllOrdersPage: React.FC = () => {
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isSuperAdmin = (adminUser.type === 'admin_employee' && adminUser.role === 'super_admin') || !adminUser.type;
  const canUploadSlip = isSuperAdmin || adminUser.permissions?.approve_slip_upload === true;
  const canSkipVerification = isSuperAdmin || adminUser.permissions?.approve_slip_no_verify === true;
  const canCancelOrder = isSuperAdmin || adminUser.permissions?.cancel_order === true;

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', merchant_id: '', payment_type: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [merchants, setMerchants] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showSlipUpload, setShowSlipUpload] = useState(false);
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string>('');
  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [slipError, setSlipError] = useState<{
    message: string;
    duplicate_order_number?: string;
    duplicate_order_id?: string;
    duplicate_order_status?: string;
    requires_confirmation?: boolean;
    verification_error?: string;
  } | null>(null);
  const [lastSatangValue, setLastSatangValue] = useState<boolean>(true);
  const [uploadMode, setUploadMode] = useState<'satang' | 'no-satang' | 'force' | 'skip-all' | null>(null);
  const [sendingWebhook, setSendingWebhook] = useState(false);
  const [previewSlipUrl, setPreviewSlipUrl] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Order | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const { currentPage, pageSize, totalItems, setTotalItems, setCurrentPage, serverPaginationProps } = useServerPagination(10);

  const [summaryData, setSummaryData] = useState({
    paid_base_amount: 0, paid_fee_amount: 0, paid_net_amount: 0, paid_count: 0, total_count: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [realtimeMode, setRealtimeMode] = useState<boolean>(true);
  const [bufferedNewOrders, setBufferedNewOrders] = useState<number>(0);
  const [bufferedNewOrderDetails, setBufferedNewOrderDetails] = useState<any[]>([]);

  const toggleRealtimeMode = () => {
    if (!realtimeMode && bufferedNewOrders > 0) {
      loadOrders(true);
      loadSummary(true);
      setBufferedNewOrders(0);
      setBufferedNewOrderDetails([]);
      toast.info(`ดึงข้อมูลใหม่ พร้อมอัพเดท ${bufferedNewOrders} รายการล่าสุด`);
    }
    setRealtimeMode(!realtimeMode);
  };

  const { notifyNewOrder, notifyPaid } = useOrderNotification();

  const [dateFrom, setDateFrom] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().format('YYYY-MM-DD'));

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success('คัดลอก Payment URL แล้ว!');
        return;
      }
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.left = '-999999px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      try {
        document.execCommand('copy');
        toast.success('คัดลอก Payment URL แล้ว!');
      } catch {
        toast.error('ไม่สามารถคัดลอกได้');
      } finally {
        document.body.removeChild(el);
      }
    } catch {
      toast.error('ไม่สามารถคัดลอกได้');
    }
  };

  const loadMerchants = async () => {
    try {
      const response = await merchantApi.get('/merchants');
      setMerchants(response.data.data);
    } catch { /* silent */ }
  };

  const loadSummary = useCallback(async (noCache = false) => {
    try {
      setSummaryLoading(true);
      let url = `/admin/orders/summary?`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.merchant_id) url += `&merchant_id=${filters.merchant_id}`;
      if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`;
      if (appliedDateTo) url += `&date_to=${appliedDateTo}`;
      if (noCache) url += `&no_cache=true`;
      const response = await merchantApi.get(url);
      if (response.data.success) {
        setSummaryData(response.data.data);
        if (noCache) toast.success('อัพเดทยอดล่าสุดแล้ว');
      }
    } catch { /* silent */ }
    finally { setSummaryLoading(false); }
  }, [filters.status, filters.merchant_id, appliedDateFrom, appliedDateTo]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const loadOrders = useCallback(async (showLoading = true) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      if (showLoading) setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      let url = `/admin/orders?limit=${pageSize}&offset=${offset}`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.merchant_id) url += `&merchant_id=${filters.merchant_id}`;
      if (filters.payment_type) url += `&payment_type=${filters.payment_type}`;
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      } else {
        if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`;
        if (appliedDateTo) url += `&date_to=${appliedDateTo}`;
      }
      const response = await merchantApi.get(url, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setOrders(response.data.data);
        setTotalItems(response.data.pagination?.total || 0);
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError' && error?.code !== 'ERR_CANCELED') {
        toast.error('ไม่สามารถโหลดข้อมูลได้');
      }
    } finally {
      if (!controller.signal.aborted && showLoading) setLoading(false);
    }
  }, [filters.status, filters.merchant_id, filters.payment_type, searchQuery, currentPage, pageSize, appliedDateFrom, appliedDateTo]);

  const silentRefresh = useCallback(() => loadOrders(false), [loadOrders]);

  const loadOrdersRef = useRef(loadOrders);
  const silentRefreshRef = useRef(silentRefresh);
  useEffect(() => { loadOrdersRef.current = loadOrders; silentRefreshRef.current = silentRefresh; }, [loadOrders, silentRefresh]);

  const loadSummaryRef = useRef(loadSummary);
  useEffect(() => { loadSummaryRef.current = loadSummary; }, [loadSummary]);

  useEffect(() => { loadMerchants(); }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const prevFiltersRef = useRef({ filters, searchQuery });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged = prev.filters !== filters || prev.searchQuery !== searchQuery;
    if (filtersChanged) {
      prevFiltersRef.current = { filters, searchQuery };
      if (currentPage !== 1) { setCurrentPage(1); return; }
    }
    loadOrders();
  }, [loadOrders, currentPage, filters, searchQuery]);

  const handleDateSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const countedOrderIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    soundService.loadSettings();

    const handleOrderCreated = (order: any) => {
      if (realtimeMode) {
        notifyNewOrder({ order_number: order.order_number, total_amount: order.total_amount });
        setCurrentPage(prevPage => {
          if (prevPage !== 1) return prevPage;
          setOrders(prev => {
            const statusOk = !filters.status || filters.status === order.status;
            const merchantOk = !filters.merchant_id || filters.merchant_id === order.merchant_id;
            const searchOk = !searchQuery.trim();
            const paymentTypeOk = !filters.payment_type ||
              (filters.payment_type === 'auto' && !order.manual_approved_by) ||
              (filters.payment_type === 'manual' && order.manual_approved_by);
            if (!statusOk || !merchantOk || !searchOk || !paymentTypeOk) return prev;
            if (prev.some(o => o.id === order.id)) return prev;
            return [order, ...prev.slice(0, pageSize - 1)];
          });
          return prevPage;
        });
        setTotalItems(prev => prev + 1);
      } else {
        setBufferedNewOrders(prev => prev + 1);
        setBufferedNewOrderDetails(prev => [...prev.slice(-9), order]);
        notifyNewOrder({ order_number: order.order_number, total_amount: order.total_amount });
      }
    };

    const handleOrderUpdated = (data: any) => {
      if (realtimeMode) {
        setOrders(prev => prev.map(o => o.id === data.id ? { ...o, ...data } : o));
        if (data.status === 'paid') {
          notifyPaid({ order_number: data.order_number, amount: data.base_amount || data.total_amount || 0 });
          if (!countedOrderIdsRef.current.has(data.id)) {
            countedOrderIdsRef.current.add(data.id);
            setSummaryData(prev => ({
              ...prev,
              paid_count: prev.paid_count + 1,
              paid_base_amount: prev.paid_base_amount + (data.base_amount || 0),
              paid_fee_amount: prev.paid_fee_amount + (data.fee_amount || 0),
              paid_net_amount: prev.paid_net_amount + (data.net_amount || 0),
            }));
          }
        }
      } else {
        setBufferedNewOrders(prev => prev + 1);
        if (data.status === 'paid') {
          notifyPaid({ order_number: data.order_number, amount: data.base_amount || data.total_amount || 0 });
        }
      }
    };

    socketService.on('order-created', handleOrderCreated);
    socketService.on('order-updated', handleOrderUpdated);
    return () => {
      socketService.off('order-created', handleOrderCreated);
      socketService.off('order-updated', handleOrderUpdated);
    };
  }, [pageSize, filters.status, filters.merchant_id, filters.payment_type, searchQuery, notifyNewOrder, notifyPaid, realtimeMode]);

  const handleSlipFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSlipFile(file);
      setSlipError(null);
      const reader = new FileReader();
      reader.onloadend = () => setSlipPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadSlip = async (
    orderId: string,
    forceApprove = false,
    satang?: boolean,
    skipAllVerification = false,
  ) => {
    if (!slipFile || !slipPreview) { toast.error('กรุณาเลือกรูปสลิป'); return; }
    setUploadingSlip(true);
    const actualSatang = forceApprove ? lastSatangValue : (satang ?? true);
    if (skipAllVerification) setUploadMode('skip-all');
    else if (forceApprove) setUploadMode('force');
    else setUploadMode(actualSatang ? 'satang' : 'no-satang');
    if (!forceApprove) { setSlipError(null); setLastSatangValue(actualSatang); }
    try {
      const response = await merchantApi.post(`/admin/orders/${orderId}/approve-slip`, {
        slip_base64: slipPreview, force_approve: forceApprove, satang: actualSatang, skip_all_verification: skipAllVerification,
      });
      if (response.data.requires_confirmation) {
        setSlipError({
          message: response.data.message,
          requires_confirmation: true,
          verification_error: response.data.verification_error,
        });
        return;
      }
      toast.success('อัพโหลดสลิปและอนุมัติสำเร็จ!');
      setShowSlipUpload(false);
      setSlipFile(null);
      setSlipPreview('');
      setSelectedOrder(null);
      loadOrders();
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData?.duplicate_order_number) {
        setSlipError({
          message: errorData.message,
          duplicate_order_number: errorData.duplicate_order_number,
          duplicate_order_id: errorData.duplicate_order_id,
          duplicate_order_status: errorData.duplicate_order_status,
        });
      } else {
        toast.error(errorData?.message || 'เกิดข้อผิดพลาด');
      }
    } finally {
      setUploadingSlip(false);
      setUploadMode(null);
    }
  };

  const openSlipUpload = (order: Order) => {
    setSelectedOrder(order);
    setShowSlipUpload(true);
    setSlipError(null);
    setSlipFile(null);
    setSlipPreview('');
    setUploadMode(null);
  };

  const resendOrderWebhook = async (order: Order) => {
    setSendingWebhook(true);
    try {
      const response = await merchantApi.post('/admin/resend-webhook', { order_id: order.id, order_number: order.order_number });
      if (response.data.success) toast.success('ส่ง Webhook สำเร็จ!');
      else toast.error(response.data.message || 'ส่ง Webhook ล้มเหลว');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSendingWebhook(false);
    }
  };

  const openCancel = (order: Order) => {
    setCancelTarget(order);
    setCancelReason('');
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const response = await merchantApi.post(`/admin/orders/${cancelTarget.id}/cancel`, {
        reason: cancelReason.trim() || undefined,
      });
      if (response.data.success) {
        toast.success(response.data.message || 'ยกเลิก Order สำเร็จ');
        setCancelTarget(null);
        loadOrders(true);
        loadSummary(true);
      } else {
        toast.error(response.data.message || 'ยกเลิกไม่สำเร็จ');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการยกเลิก Order');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status: string, expiresAt?: string) => {
    const isCountdown = status === 'pending' && !!expiresAt && new Date(expiresAt).getTime() > Date.now();
    return (
      <StatusBadge status={status} statusMap={ORDER_STATUS_MAP} pulse={isCountdown}>
        {isCountdown ? <CountdownTimer expiresAt={expiresAt!} /> : undefined}
      </StatusBadge>
    );
  };

  const { paid_base_amount, paid_fee_amount, paid_net_amount, paid_count } = summaryData;

  const columns: Column<Order>[] = [
    {
      key: 'order_number',
      header: 'Order',
      sortable: true,
      render: (order) => (
        <div>
          <p
            className="font-semibold text-gray-800 dark:text-slate-100 text-sm cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            onClick={() => { navigator.clipboard.writeText(order.order_number || ''); toast.success('คัดลอก Order แล้ว', { autoClose: 1000 }); }}
          >
            {order.order_number}
            {(order as any).deposit_order_id && (
              <span className="ml-1.5 text-[9px] px-1 py-px rounded font-medium bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 align-middle"
                title="มาจาก P2P slip-order ที่จับคู่ไม่ได้ → เข้าบัญชีเว็บ (คิด fee ปกติ)">P2P→เว็บ</span>
            )}
          </p>
          {order.reference_id && (
            <p
              className="text-xs text-gray-400 dark:text-slate-500 cursor-pointer hover:text-blue-400 transition-colors"
              onClick={() => { navigator.clipboard.writeText(order.reference_id || ''); toast.success('คัดลอก Ref แล้ว', { autoClose: 1000 }); }}
            >Ref: {order.reference_id}</p>
          )}
        </div>
      ),
    },
    {
      key: 'merchant',
      header: 'Merchant',
      render: (order) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{(order as any).merchant?.name || '-'}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">{(order as any).merchant?.email || ''}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'bank_info',
      header: 'ธนาคาร',
      render: (order) => {
        const match = (order as any).sender_account_match;
        const bankKey = (order.bank_name || '').toUpperCase().replace('CIMBT', 'CIMB').replace('UOBT', 'UOB').replace('LH BANK', 'LHB') as keyof typeof bankLogos;
        const bankInfo = bankLogos[bankKey];
        return (
          <div>
            <div className="flex items-center gap-1.5">
              {bankInfo && <img src={bankInfo.icon} alt={order.bank_name || ''} className="w-6 h-6 rounded-full object-contain" />}
              <span className="font-medium text-gray-700 dark:text-slate-200">{order.bank_name || '-'}</span>
            </div>
            <small className="text-gray-500 dark:text-slate-400">{order.bank_number || '-'}</small>
            {order.status === 'paid' && (
              <div className="mt-0.5">
                {match === true && <span className="text-[10px] font-semibold text-green-600 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">✓ บัญชีตรง</span>}
                {match === false && <span className="text-[10px] font-semibold text-red-500 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded-full">✗ บัญชีไม่ตรง</span>}
                {match === null && <span className="text-[10px] text-gray-400 dark:text-slate-500">ข้อมูลไม่ครบ</span>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'base_amount',
      header: 'ยอดฝาก',
      sortable: true,
      render: (order) => (
        <div>
          <p className="font-bold text-sm" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(order.base_amount)}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">รวม: ฿{fmtMoney(order.total_amount)}</p>
          {order.amount_mismatch && (
            <span
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-orange-600 bg-orange-50 dark:bg-orange-900/30 px-1.5 py-0.5 rounded-full cursor-help"
              title={`ยอดสลิปไม่ตรง: เรียกเก็บ ฿${fmtMoney(order.requested_amount || 0)} → สลิปจ่าย ฿${fmtMoney(order.slip_amount || 0)} (${slipMismatchInfo(order).word} ฿${fmtMoney(slipMismatchInfo(order).absDelta)})`}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} /> {slipMismatchInfo(order).badge}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'fee_amount',
      header: 'Fee',
      sortable: true,
      render: (order) => (
        order.fee_amount !== undefined && order.fee_amount > 0
          ? <span className="text-amber-600 font-semibold text-sm">-฿{fmtMoney(order.fee_amount)}</span>
          : <span className="text-gray-300 dark:text-slate-600">-</span>
      ),
    },
    {
      key: 'net_amount',
      header: 'สุทธิ',
      sortable: true,
      render: (order) => (
        order.net_amount !== undefined && order.net_amount > 0
          ? <span className="text-green-600 font-bold text-sm">฿{fmtMoney(order.net_amount)}</span>
          : <span className="text-gray-300 dark:text-slate-600">-</span>
      ),
    },
    {
      key: 'customer_name',
      header: 'ลูกค้า',
      render: (order) => <span className="text-gray-700 dark:text-slate-200 text-sm">{order.customer_name || '-'}</span>,
    },
    {
      key: 'status',
      header: 'สถานะ',
      sortable: true,
      render: (order) => getStatusBadge(order.status, order.expires_at),
    },
    {
      key: 'slip_url',
      header: 'สลิป',
      render: (order) => (
        order.slip_url ? (
          <img
            src={formatSlipUrl(order.slip_url)}
            alt="Slip"
            className="w-16 h-auto rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
            onClick={(e) => { e.stopPropagation(); setPreviewSlipUrl(formatSlipUrl(order.slip_url!)); }}
          />
        ) : <span className="text-gray-300 dark:text-slate-600">-</span>
      ),
    },
    {
      key: 'admin_action',
      header: 'Admin Action',
      render: (order) => {
        if (!isAdminApproved(order.manual_approved_by)) return <span className="text-gray-300 dark:text-slate-600">-</span>;
        const method = order.slip_approval_method ? SLIP_METHOD_MAP[order.slip_approval_method] : null;
        return (
          <div className="flex flex-col items-start gap-1">
            <span
              data-tooltip-id="order-tooltip"
              data-tooltip-content={order.manual_approved_at ? `อนุมัติเมื่อ: ${dayjs(order.manual_approved_at).format('DD/MM/YYYY HH:mm:ss')}` : 'แอดมินอัพสลิปและอนุมัติรายการนี้'}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap cursor-help bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
            >
              <FontAwesomeIcon icon={faUserShield} />อัพสลิปให้
            </span>
            <span className="text-[10px] text-gray-500 dark:text-slate-400 break-all leading-tight">{order.manual_approved_by}</span>
            {method && (
              <span
                data-tooltip-id="order-tooltip"
                data-tooltip-content={method.detail}
                className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap cursor-help ${method.className}`}
              >
                {method.label}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (order) => (
        <div className="flex flex-nowrap gap-1.5">
          <button
            data-tooltip-id="order-tooltip"
            data-tooltip-content="คัดลอกลิงก์ชำระเงิน"
            onClick={(e) => { e.stopPropagation(); copyToClipboard(`${window.location.origin}/pay/${order.order_number}`); }}
            className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 rounded-lg text-xs font-medium transition-colors"
          >
            <FontAwesomeIcon icon={faLink} />
          </button>
          <button
            data-tooltip-id="order-tooltip"
            data-tooltip-content="ดูรายละเอียดออเดอร์"
            onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}
            className="px-2.5 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg text-xs font-medium transition-colors"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          {canUploadSlip && (order.status === 'expired' || order.status === 'cancelled' || order.status === 'pending') && (
            <button
              data-tooltip-id="order-tooltip"
              data-tooltip-content="อัปโหลดสลิปธนาคาร"
              onClick={(e) => { e.stopPropagation(); openSlipUpload(order); }}
              className="px-2.5 py-1.5 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg text-xs font-medium transition-colors"
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </button>
          )}
          {order.status === 'paid' && (
            <button
              data-tooltip-id="order-tooltip"
              data-tooltip-content="ส่ง Webhook"
              onClick={(e) => { e.stopPropagation(); resendOrderWebhook(order); }}
              className="px-2.5 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-600 rounded-lg text-xs font-medium transition-colors"
              disabled={sendingWebhook}
            >
              <FontAwesomeIcon icon={faBell} />
            </button>
          )}
          {canCancelOrder && order.status === 'pending' && (
            <button
              data-tooltip-id="order-tooltip"
              data-tooltip-content="ยกเลิก Order"
              onClick={(e) => { e.stopPropagation(); openCancel(order); }}
              className="px-2.5 py-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg text-xs font-medium transition-colors"
            >
              <FontAwesomeIcon icon={faBan} />
            </button>
          )}
          <Tooltip
            id="order-tooltip"
            style={{ backgroundColor: '#1f2937', color: '#fff', fontSize: '10px', borderRadius: '6px', zIndex: 100 }}
          />
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'วันที่สร้าง',
      sortable: true,
      render: (order) => (
        <span className="text-gray-700 dark:text-slate-300 text-[12px]">{dayjs(order.created_at).format('DD/MM/YYYY HH:mm:ss')}</span>
      ),
    },
  ];

  const summaryCards: StatCardDef[] = [
    {
      icon: faClipboardList,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-accent)' },
      label: 'ยอดฝากรวม (Paid)',
      value: <span style={{ color: 'var(--theme-accent)' }}>฿{paid_base_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>,
      subValue: <span className="text-xs text-gray-400">{paid_count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faExclamationTriangle,
      iconClassName: 'bg-amber-500 text-white',
      label: 'ค่าธรรมเนียม',
      value: <span className="text-amber-600">-฿{paid_fee_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-green-500 text-white',
      label: 'ยอดสุทธิ',
      value: <span className="text-green-600">฿{paid_net_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>,
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faClipboardList}
        title="All Orders"
        subtitle={`รายการ Orders ทั้งหมด • ${totalItems.toLocaleString()} รายการ`}
        below={<LiveViewToggle realtimeMode={realtimeMode} onToggle={toggleRealtimeMode} />}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleDateSearch}
        loading={loading}
        actions={
          <button
            onClick={() => loadOrders()}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faRotate} /> Refresh
          </button>
        }
      >
        {/* Text search */}
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadOrders()}
              placeholder="ค้นหา Order, Ref, ลูกค้า, ยอดเงิน..."
              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300">
                <FontAwesomeIcon icon={faXmark} className="text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* Status */}
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faFilter} className="mr-1" />
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Merchant */}
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Merchant</label>
          <MerchantMultiSelect
            merchants={merchants}
            value={filters.merchant_id}
            onChange={(id) => setFilters({ ...filters, merchant_id: id })}
            placeholder="All Merchants"
            variant="light"
          />
        </div>

        {/* Payment Type */}
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Payment Type</label>
          <select
            value={filters.payment_type}
            onChange={(e) => setFilters({ ...filters, payment_type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
          >
            <option value="">ทั้งหมด (Auto+Manual)</option>
            <option value="auto">Auto (Bank API)</option>
            <option value="manual">Manual Slip</option>
          </select>
        </div>

      </UiFilterPanel>

      {/* Summary controls */}
      <div className="flex justify-end mb-2 gap-2">
        <LiveModeBufferBadge
          count={bufferedNewOrders}
          onFlush={() => {
            loadOrders(true);
            loadSummary(true);
            setBufferedNewOrders(0);
            setBufferedNewOrderDetails([]);
            toast.success('อัพเดทข้อมูลแล้ว');
          }}
        />
        <button
          onClick={() => loadSummary(true)}
          disabled={summaryLoading}
          className="px-4 py-2 rounded-xl text-xs font-semibold bg-white border border-gray-200 text-gray-600
                     hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:border-green-300 hover:text-green-700
                     dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:border-green-700 dark:hover:text-green-400
                     transition-all flex items-center gap-2 shadow-sm hover:shadow-green-500/20"
        >
          <FontAwesomeIcon icon={faRotate} className={`text-sm ${summaryLoading ? 'animate-spin' : 'text-green-500'}`} />
          <span>เช็คยอดล่าสุด</span>
        </button>
      </div>

      <UiStatsGrid cards={summaryCards} loading={summaryLoading} cols={3} />

      <DataTable
        data={orders}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มี Orders"
        emptyIcon={<FontAwesomeIcon icon={faClipboardList} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={serverPaginationProps}
        hoverable
      />

      {/* Order Detail Modal */}
      <UiModal
        open={!!(selectedOrder && !showSlipUpload)}
        onClose={() => setSelectedOrder(null)}
        title="รายละเอียด Order"
        icon={faFileAlt}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
      >
        {selectedOrder && (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-0.5">Order Number</p>
                <p className="font-mono font-bold text-gray-800 dark:text-slate-100 text-sm break-all">{selectedOrder.order_number}</p>
              </div>
              <div className="shrink-0">{getStatusBadge(selectedOrder.status)}</div>
            </div>

            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3.5 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 dark:text-slate-400 text-xs">Merchant</span>
                <span className="font-semibold text-gray-700 dark:text-slate-200">{(selectedOrder as any).merchant?.name || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 dark:text-slate-400 text-xs">Email</span>
                <span className="text-gray-600 dark:text-slate-300 text-xs">{(selectedOrder as any).merchant?.email || '-'}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-slate-600 pt-2 flex flex-wrap gap-x-6 gap-y-1">
                <div className="text-xs text-gray-400 dark:text-slate-400">สร้างเมื่อ: <span className="text-gray-600 dark:text-slate-300">{new Date(selectedOrder.created_at).toLocaleString('th-TH')}</span></div>
                {selectedOrder.paid_at && (
                  <div className="text-xs text-green-500">ชำระเมื่อ: <span className="text-green-600 font-medium">{new Date(selectedOrder.paid_at).toLocaleString('th-TH')}</span></div>
                )}
                {selectedOrder.manual_approved_at && (
                  <div className="text-xs text-amber-500">อนุมัติด้วยตนเอง: {new Date(selectedOrder.manual_approved_at).toLocaleString('th-TH')}</div>
                )}
                {(selectedOrder as any).cancelled_at && (
                  <div className="text-xs text-red-500">ยกเลิกเมื่อ: <span className="text-red-600 font-medium">{new Date((selectedOrder as any).cancelled_at).toLocaleString('th-TH')}</span></div>
                )}
              </div>
            </div>

            {selectedOrder.status === 'cancelled' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3.5 space-y-2">
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faTimesCircle} /> ข้อมูลการยกเลิก
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 dark:text-slate-400 text-xs">ยกเลิกโดย</span>
                  <span className="font-medium text-gray-700 dark:text-slate-200">{(selectedOrder as any).cancelled_by || '-'}</span>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-0.5">เหตุผล</p>
                  <p className="text-sm text-gray-700 dark:text-slate-200 whitespace-pre-wrap break-words">
                    {(selectedOrder as any).cancel_reason || <span className="text-gray-400 italic">ไม่ได้ระบุเหตุผล</span>}
                  </p>
                </div>
              </div>
            )}

            <div className="rounded-xl p-4" style={{ background: 'var(--theme-primary-light)' }}>
              <p className="text-xs font-medium mb-3" style={{ color: 'var(--theme-primary)' }}>💰 ยอดเงิน</p>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-400">ยอดจ่ายจริง</p>
                  <p className="text-3xl font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(selectedOrder.total_amount)}</p>
                </div>
                <div className="text-right text-xs text-gray-500 dark:text-slate-400 space-y-0.5">
                  <p>Base: <span className="font-semibold text-gray-700 dark:text-slate-200">฿{fmtMoney(selectedOrder.base_amount)}</span></p>
                  <p>สตางค์: <span className="font-semibold text-gray-700 dark:text-slate-200">{selectedOrder.cent_modifier > 0 ? '+' : ''}{fmtMoney(selectedOrder.cent_modifier)}</span></p>
                </div>
              </div>
              {selectedOrder.amount_mismatch && (
                <div className="border-t border-black/5 pt-3 mb-1 flex items-start gap-2 text-xs text-orange-700 dark:text-orange-300">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 flex-shrink-0" />
                  <span>สลิปยอด{slipMismatchInfo(selectedOrder).word} — เรียกเก็บเดิม <b>฿{fmtMoney(selectedOrder.requested_amount || 0)}</b> → สลิปจ่าย <b>฿{fmtMoney(selectedOrder.slip_amount || 0)}</b> ({slipMismatchInfo(selectedOrder).word} ฿{fmtMoney(slipMismatchInfo(selectedOrder).absDelta)})</span>
                </div>
              )}
              {selectedOrder.fee_amount !== undefined && selectedOrder.fee_amount > 0 && (
                <div className="border-t border-black/5 pt-3 flex items-center justify-between text-sm">
                  <span className="text-xs text-gray-500 dark:text-slate-400">ค่าธรรมเนียม: <span className="text-amber-600 font-semibold">-฿{fmtMoney(selectedOrder.fee_amount)}</span></span>
                  <span className="text-xs text-gray-500 dark:text-slate-400">ยอดสุทธิ: <span className="text-green-600 font-bold text-base">฿{fmtMoney(selectedOrder.net_amount || 0)}</span></span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {(selectedOrder.customer_name || selectedOrder.customer_email) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3.5 space-y-1.5">
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">👤 ลูกค้า</p>
                  {selectedOrder.customer_name && (
                    <div><p className="text-[10px] text-gray-400 dark:text-slate-500">ชื่อ</p><p className="text-sm font-medium text-gray-700 dark:text-slate-200">{selectedOrder.customer_name}</p></div>
                  )}
                  {selectedOrder.customer_email && (
                    <div><p className="text-[10px] text-gray-400 dark:text-slate-500">Email</p><p className="text-xs text-gray-600 dark:text-slate-300 break-all">{selectedOrder.customer_email}</p></div>
                  )}
                  {(selectedOrder as any).customer_phone && (
                    <div><p className="text-[10px] text-gray-400 dark:text-slate-500">เบอร์โทร</p><p className="text-sm text-gray-700 dark:text-slate-200">{(selectedOrder as any).customer_phone}</p></div>
                  )}
                </div>
              )}
              {((selectedOrder as any).bank_name || (selectedOrder as any).bank_number) && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3.5 space-y-1.5">
                  <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">🏦 ธนาคาร</p>
                  {(selectedOrder as any).bank_name && (
                    <div><p className="text-[10px] text-gray-400 dark:text-slate-500">ธนาคาร</p><span className="inline-block px-2 py-0.5 bg-white dark:bg-slate-700 rounded text-sm font-semibold text-gray-700 dark:text-slate-200">{(selectedOrder as any).bank_name}</span></div>
                  )}
                  {(selectedOrder as any).bank_number && (
                    <div><p className="text-[10px] text-gray-400 dark:text-slate-500">เลขบัญชี</p><span className="font-mono text-sm text-gray-700 dark:text-slate-200">{(selectedOrder as any).bank_number}</span></div>
                  )}
                </div>
              )}
            </div>

            {selectedOrder.status === 'paid' && (
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3.5 space-y-2">
                <p className="text-xs font-semibold text-gray-600 dark:text-slate-300">🏧 ข้อมูลผู้โอน</p>
                {(selectedOrder as any).transaction_data?.from_account && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 dark:text-slate-400 text-xs">บัญชีผู้โอน</span>
                    <code className="px-2 py-0.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded text-xs text-gray-700 dark:text-slate-200">
                      {(selectedOrder as any).transaction_data.from_account}
                    </code>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 dark:text-slate-400 text-xs">สถานะบัญชี</span>
                  {(selectedOrder as any).sender_account_match === true && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">✓ ข้อมูลบัญชีตรง</span>}
                  {(selectedOrder as any).sender_account_match === false && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">✗ ข้อมูลบัญชีไม่ตรง</span>}
                  {(selectedOrder as any).sender_account_match === null && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-300">— ข้อมูลไม่ครบ</span>}
                </div>
              </div>
            )}

            {isAdminApproved(selectedOrder.manual_approved_by) && (() => {
              const method = selectedOrder.slip_approval_method ? SLIP_METHOD_MAP[selectedOrder.slip_approval_method] : null;
              return (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3.5 space-y-2">
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faUserShield} /> แอดมินทำรายการ (อัพสลิปให้)
                  </p>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-gray-400 dark:text-slate-400 text-xs shrink-0">โดย</span>
                    <span className="font-medium text-gray-700 dark:text-slate-200 text-xs break-all text-right">{selectedOrder.manual_approved_by}</span>
                  </div>
                  {selectedOrder.manual_approved_at && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400 dark:text-slate-400 text-xs">อนุมัติเมื่อ</span>
                      <span className="text-gray-600 dark:text-slate-300 text-xs">{dayjs(selectedOrder.manual_approved_at).format('DD/MM/YYYY HH:mm:ss')}</span>
                    </div>
                  )}
                  <div className="border-t border-amber-100 dark:border-amber-800 pt-2">
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">การตรวจสอบ</p>
                    {method ? (
                      <p className="text-xs text-gray-700 dark:text-slate-200 leading-relaxed">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium mr-1.5 ${method.className}`}>{method.label}</span>
                        {method.detail}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-700 dark:text-slate-200 leading-relaxed">ไม่ระบุวิธีตรวจ (รายการเก่าก่อนระบบบันทึกวิธีตรวจ)</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {selectedOrder.slip_url && (
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3.5">
                <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 mb-3 flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faPaperclip} /> สลิป
                </p>
                <img src={formatSlipUrl(selectedOrder.slip_url)} alt="Slip" className="max-w-full rounded-lg" />
              </div>
            )}

            <SlipAttemptTimeline fetchUrl={slipAttemptUrls.adminOrder(selectedOrder.id)} isAdmin />

            {(selectedOrder as any).transaction_data && (
              <div className="bg-gray-900 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-400 flex items-center gap-1.5 mb-3">
                  <FontAwesomeIcon icon={faCreditCard} /> Raw Data
                </p>
                <pre className="text-xs text-gray-400 overflow-x-auto max-h-36 overflow-y-auto leading-relaxed">
                  {JSON.stringify((selectedOrder as any).transaction_data, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}
      </UiModal>

      {/* Slip Upload Modal */}
      <UiModal
        open={showSlipUpload && !!selectedOrder}
        onClose={() => setShowSlipUpload(false)}
        title="อัพโหลดสลิป"
        icon={faPaperclip}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
      >
        {selectedOrder && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-1 text-sm">
              <p><span className="text-gray-500 dark:text-slate-400">Order:</span> <strong className="dark:text-slate-100">{selectedOrder.order_number}</strong></p>
              <p><span className="text-gray-500 dark:text-slate-400">Merchant:</span> <span className="dark:text-slate-200">{(selectedOrder as any).merchant?.name}</span></p>
              <p><span className="text-gray-500 dark:text-slate-400">ยอดเงิน:</span> <strong className="text-lg" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(selectedOrder.total_amount)}</strong></p>
              <p><span className="text-gray-500 dark:text-slate-400">สถานะ:</span> {getStatusBadge(selectedOrder.status)}</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
                <span><strong>Admin Approval:</strong> การอัพโหลดจะอนุมัติ Order เป็น Paid และส่ง Webhook ทันที</span>
              </p>
            </div>

            {slipError?.duplicate_order_number && (
              <div className="bg-red-50 border border-red-300 rounded-xl p-4">
                <p className="text-sm text-red-800 font-semibold flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} />{slipError.message}
                </p>
                <div className="text-sm text-red-700 space-y-1 bg-red-100/50 rounded-lg p-3">
                  <p><span className="text-red-500">Order ที่ซ้ำ:</span> <strong>{slipError.duplicate_order_number}</strong></p>
                  <p><span className="text-red-500">Order ID:</span> <code className="bg-red-200/50 px-1 rounded text-xs">{slipError.duplicate_order_id}</code></p>
                  <p><span className="text-red-500">สถานะ:</span> <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{slipError.duplicate_order_status}</span></p>
                </div>
              </div>
            )}

            {slipError?.requires_confirmation && (
              <div className="bg-orange-50 border border-orange-300 rounded-xl p-4">
                <p className="text-sm text-orange-800 font-semibold flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} />{slipError.message}
                </p>
                {slipError.verification_error && (
                  <div className="text-sm text-orange-700 bg-orange-100/50 rounded-lg p-3 mb-3">
                    <p><span className="text-orange-600">รายละเอียด:</span> {slipError.verification_error}</p>
                  </div>
                )}
                <p className="text-sm text-orange-700 mb-3">ต้องการดำเนินการต่อหรือไม่?</p>
                <button
                  onClick={() => uploadSlip(selectedOrder.id, true)}
                  disabled={uploadingSlip}
                  className="w-full py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={uploadMode === 'force' ? faHourglass : faUpload} className={uploadMode === 'force' ? 'animate-spin' : ''} />
                  {uploadMode === 'force' ? 'กำลังดำเนินการ...' : 'ยืนยันอนุมัติต่อ'}
                </button>
              </div>
            )}

            <label className="block cursor-pointer">
              {slipPreview ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-dashed border-green-300 bg-green-50">
                  <img src={slipPreview} alt="Preview" className="w-full max-h-64 object-contain" />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm">คลิกเพื่อเปลี่ยนรูป</span>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-200 dark:border-slate-600 rounded-xl p-8 text-center hover:border-gray-300 dark:hover:border-slate-500 transition-colors">
                  <FontAwesomeIcon icon={faCamera} className="text-4xl text-gray-300 dark:text-slate-600 mb-2" />
                  <p className="text-gray-500 dark:text-slate-400 text-sm">คลิกเพื่อเลือกรูปสลิป</p>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleSlipFileChange} className="hidden" />
            </label>

            {(!slipError || (!slipError.requires_confirmation && !slipError.duplicate_order_number)) && (
              <div className="flex flex-col gap-3">
                <div>
                  <button
                    onClick={() => uploadSlip(selectedOrder.id)}
                    disabled={!slipFile || uploadingSlip}
                    className="w-full py-3 rounded-xl text-white font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'var(--theme-primary-gradient)' }}
                  >
                    <FontAwesomeIcon icon={uploadMode === 'satang' ? faHourglass : faUpload} className={uploadMode === 'satang' ? 'animate-spin' : ''} />
                    {uploadMode === 'satang' ? 'กำลังอัพโหลด...' : 'อนุมัติและส่ง Webhook'}
                  </button>
                  <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1 text-center">✓ เช็คเลขบัญชีผู้รับ ✓ เช็คธนาคาร ✓ เช็คยอดเงิน+สตางค์</p>
                </div>

                <div>
                  <button
                    onClick={() => uploadSlip(selectedOrder.id, false, false)}
                    disabled={!slipFile || uploadingSlip}
                    className="w-full text-[14px] py-3 rounded-xl text-white font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'var(--theme-accent)' }}
                  >
                    <FontAwesomeIcon icon={uploadMode === 'no-satang' ? faHourglass : faUpload} className={uploadMode === 'no-satang' ? 'animate-spin' : ''} />
                    {uploadMode === 'no-satang' ? 'กำลังอัพโหลด...' : 'อนุมัติ (ไม่เช็คสตางค์) และส่ง Webhook'}
                  </button>
                  <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1 text-center">✓ เช็คเลขบัญชีผู้รับ ✓ เช็คธนาคาร ✓ เช็คยอดเงิน (ไม่สนเศษสตางค์)</p>
                </div>

                {canSkipVerification && (
                  <div>
                    <button
                      onClick={() => uploadSlip(selectedOrder.id, false, true, true)}
                      disabled={!slipFile || uploadingSlip}
                      className="w-full py-2.5 rounded-xl text-white font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-red-500 hover:bg-red-600"
                    >
                      <FontAwesomeIcon icon={uploadMode === 'skip-all' ? faHourglass : faExclamationTriangle} className={uploadMode === 'skip-all' ? 'animate-spin' : ''} />
                      {uploadMode === 'skip-all' ? 'กำลังอัพโหลด...' : 'อนุมัติ (ไม่เช็คความถูกต้อง)'}
                    </button>
                    <p className="text-[11px] text-red-400 mt-1 text-center">
                      ⚠️ ไม่ทำการตรวจสอบความถูกต้องใดๆ—สามารถอัปโหลดได้ทันที (หากสลิปมี Qr code ระบบจะตรวจสอบว่า Qr นี้ถูกใช้ในออเดอร์อื่นในระบบแล้วหรือไม่)
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </UiModal>

      {/* Cancel Order Modal */}
      <UiModal
        open={!!cancelTarget}
        onClose={() => { if (!cancelling) setCancelTarget(null); }}
        title="ยกเลิก Order"
        icon={faBan}
        iconStyle={{ color: '#dc2626' }}
        size="md"
      >
        {cancelTarget && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-1 text-sm">
              <p><span className="text-gray-500 dark:text-slate-400">Order:</span> <strong className="dark:text-slate-100">{cancelTarget.order_number}</strong></p>
              <p><span className="text-gray-500 dark:text-slate-400">Merchant:</span> <span className="dark:text-slate-200">{(cancelTarget as any).merchant?.name || '-'}</span></p>
              <p><span className="text-gray-500 dark:text-slate-400">ยอดเงิน:</span> <strong className="text-lg" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(cancelTarget.total_amount)}</strong></p>
              <p><span className="text-gray-500 dark:text-slate-400">สถานะ:</span> {getStatusBadge(cancelTarget.status)}</p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-800 flex items-start gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
                <span><strong>ยืนยันการยกเลิก:</strong> Order จะถูกเปลี่ยนสถานะเป็น Cancelled และส่ง Webhook <code>order.cancelled</code> (ยกเลิกได้เฉพาะ Order ที่ยังไม่ชำระเงิน)</span>
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">เหตุผล (ไม่บังคับ)</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="ระบุเหตุผลการยกเลิก เพื่อบันทึกใน log..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 hover:bg-gray-50 text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 transition-colors disabled:opacity-50"
              >
                ยกเลิก
              </button>
              <button
                onClick={confirmCancel}
                disabled={cancelling}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={cancelling ? faHourglass : faBan} className={cancelling ? 'animate-spin' : ''} />
                {cancelling ? 'กำลังยกเลิก...' : 'ยืนยันยกเลิก'}
              </button>
            </div>
          </>
        )}
      </UiModal>

      {/* Slip Preview Modal (full-screen dark overlay — intentionally not using UiModal) */}
      {previewSlipUrl && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewSlipUrl(null)}
        >
          <div className="relative">
            <button
              onClick={() => setPreviewSlipUrl(null)}
              className="absolute -top-10 -right-2 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            >
              ✕
            </button>
            <img
              src={previewSlipUrl}
              alt="Slip Preview"
              className="max-w-sm max-h-[70vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AllOrdersPage;
